import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import config from '../../config/config';
import './LoginPage.css';

export default function LoginPage() {
  const { login, register, loginWithToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState({ localEnabled: true, oidc: { enabled: false, providerName: 'Microsoft Entra ID' } });
  const [providersLoading, setProvidersLoading] = useState(true);

  const ssoToken = searchParams.get('sso_token');
  const ssoError = searchParams.get('sso_error');

  useEffect(() => {
    fetch(`${config.BASE_URL}/auth/providers`)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.error || 'Failed to load auth providers');
        return data;
      })
      .then(setProviders)
      .catch(() => setProviders({ localEnabled: true, oidc: { enabled: false, providerName: 'Microsoft Entra ID' } }))
      .finally(() => setProvidersLoading(false));
  }, []);

  useEffect(() => {
    if (!ssoError) return;
    setError(ssoError);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('sso_error');
      return next;
    }, { replace: true });
  }, [ssoError, setSearchParams]);

  useEffect(() => {
    if (!ssoToken) return;
    setLoading(true);
    loginWithToken(ssoToken)
      .then(() => navigate('/'))
      .catch((err) => setError(err.message))
      .finally(() => {
        setLoading(false);
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('sso_token');
          return next;
        }, { replace: true });
      });
  }, [ssoToken, loginWithToken, navigate, setSearchParams]);

  const oidcStartUrl = useMemo(() => {
    const frontendRedirect = `${window.location.origin}/login`;
    return `${config.BASE_URL}/auth/oidc/start?frontend_redirect=${encodeURIComponent(frontendRedirect)}`;
  }, []);

  if (isAuthenticated) {
    navigate('/');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(email, password);
      }
      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          <span className="login-logo-icon">🛡️</span>
          <span className="login-logo-text">DepGuard</span>
        </div>
        <p className="login-subtitle">SBOM &amp; Dependency Security Platform</p>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: -4, marginBottom: 18 }}>
          <button
            type="button"
            onClick={() => navigate('/website')}
            style={{
              border: 'none',
              background: 'none',
              color: '#818cf8',
              cursor: 'pointer',
              fontWeight: 600,
              padding: 0,
            }}
          >
            Bekijk de DepGuard website →
          </button>
        </div>

        <div className="login-tabs">
          <button className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')}>
            Sign In
          </button>
          <button className={mode === 'register' ? 'active' : ''} onClick={() => setMode('register')}>
            Register
          </button>
        </div>

        {providers.oidc?.enabled && (
          <div className="login-sso-section">
            <button
              type="button"
              className="login-sso-button"
              onClick={() => { window.location.href = oidcStartUrl; }}
              disabled={loading}
            >
              🏢 Continue with {providers.oidc.providerName || 'Enterprise SSO'}
            </button>
            <div className="login-divider"><span>or use local credentials</span></div>
          </div>
        )}

        {!providersLoading && providers.localEnabled && (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-field">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus={!providers.oidc?.enabled}
              />
            </div>
            <div className="login-field">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>

            {error && <div className="login-error">{error}</div>}

            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? '…' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>
        )}

        {providersLoading && <div className="login-note">Loading sign-in options…</div>}

        {mode === 'register' && (
          <p className="login-note">
            ℹ️ First account created automatically becomes Admin.
            Subsequent accounts require an existing Admin to register.
          </p>
        )}
      </div>
    </div>
  );
}
